import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Icon, Stars } from '../components/Icons'
import { usePackages } from '../hooks/usePackages'
import { findMatchingPackage } from '../utils/packageUtils'

export default function Booking() {
  const { packageName } = useParams()
  // const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('inclusions')
  const [tourDetailTab, setTourDetailTab] = useState('departure')
  const [openDay, setOpenDay] = useState(1)
  const [selectedDate, setSelectedDate] = useState('July 09')
  const [currentImgIdx, setCurrentImgIdx] = useState(0)

  useEffect(() => {
    window.scrollTo(0, 0)
    setCurrentImgIdx(0)
  }, [packageName])

  // WhatsApp Helper function
  const sendToWhatsApp = (message) => {
    const phone = '7092210922'
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`
    window.open(url, '_blank')
  }

  // State hooks for forms
  // 1. Book Your Tour Form
  const [adultsCount, setAdultsCount] = useState(1)
  const [childrenCount, setChildrenCount] = useState(0)
  const [infantCount, setInfantCount] = useState(0)

  // 2. Enquire Now Form
  const [enquireName, setEnquireName] = useState('')
  const [enquirePhone, setEnquirePhone] = useState('')
  const [enquireEmail, setEnquireEmail] = useState('')

  // 3. Footer Enquire Form
  const [footerName, setFooterName] = useState('')
  const [footerCity, setFooterCity] = useState('')
  const [footerEmail, setFooterEmail] = useState('')
  const [footerPhone, setFooterPhone] = useState('')
  const [footerDestination, setFooterDestination] = useState('')
  const [footerDate, setFooterDate] = useState('')
  const [footerPeopleCount, setFooterPeopleCount] = useState('')
  const [footerVacationType, setFooterVacationType] = useState('')
  const [footerCaptcha, setFooterCaptcha] = useState('')

  const { packages, loading } = usePackages()
  const matchedP = findMatchingPackage(packages, packageName)

  const rawP = matchedP || {
    name: packageName || 'Explore the World',
    location: 'Custom Destination',
    duration: '5 Nights / 6 Days',
    price: '₹ 25,000',
    img: 'https://images.unsplash.com/photo-1529655683826-aba9b3e77383?auto=format&fit=crop&q=80&w=1200&h=800',
  }

  const priceNumber = typeof rawP.price === 'number'
    ? rawP.price
    : parseInt(String(rawP.price || rawP.from || '25000').replace(/[^0-9]/g, '')) || 25000

  const p = {
    ...rawP,
    duration:
      rawP.duration ||
      (rawP.nights
        ? `${Number(rawP.nights) + 1} Days / ${rawP.nights} Nights`
        : '6 Days / 5 Nights'),
    location: rawP.location || rawP.city || rawP.country || rawP.region || rawP.name,
    price: rawP.from || (typeof rawP.price === 'number' ? `₹ ${rawP.price.toLocaleString('en-IN')}` : (rawP.price || '₹ 25,000')),
    img:
      rawP.image ||
      rawP.img ||
      'https://images.unsplash.com/photo-1529655683826-aba9b3e77383?auto=format&fit=crop&q=80&w=1200&h=800',
    galleryImages: rawP.galleryImages || [],
  }

  const allImages = [
    p.img,
    ...(p.galleryImages || [])
  ].filter(Boolean)

  const cleanPriceVal = String(p.price || '25,000').replace(/^[₹RM\s]*/i, '')

  const handleBookNowSubmit = (e) => {
    e.preventDefault()
    const message = `Hello DIRA HOLIDAYS! I would like to book a tour.

*Tour Details:*
- *Tour:* ${p.name}
- *Departure Date:* ${selectedDate}
- *Adults:* ${adultsCount}
- *Children:* ${childrenCount}
- *Infants:* ${infantCount}`
    sendToWhatsApp(message)
  }

  const handleEnquireSubmit = (e) => {
    e.preventDefault()
    const message = `Hello DIRA HOLIDAYS! I have an enquiry.

*Enquiry Details:*
- *Tour:* ${p.name}
- *Name:* ${enquireName}
- *Phone:* ${enquirePhone}
- *Email:* ${enquireEmail}`
    sendToWhatsApp(message)
  }

  const handleFooterEnquireSubmit = (e) => {
    e.preventDefault()
    if (footerCaptcha.trim() !== '15') {
      alert('Please solve the captcha correctly (8 + 7 = 15).')
      return
    }
    const message = `Hello DIRA HOLIDAYS! I want to book my dream vacation.

*Vacation Plan Details:*
- *Name:* ${footerName}
- *City of Residence:* ${footerCity}
- *Email:* ${footerEmail}
- *Phone Number:* ${footerPhone}
- *Travel Destination:* ${footerDestination || p.name}
- *Date of Travel:* ${footerDate}
- *No. of People:* ${footerPeopleCount}
- *Vacation Type:* ${footerVacationType}`
    sendToWhatsApp(message)
  }

  const itinerary = (p.itinerary && p.itinerary.length > 0) ? p.itinerary : [
    {
      day: 1,
      title: `Welcome to ${p.name}`,
      content: `Arrival at airport/station. Transfer to your premium hotel in ${p.name}. Evening at leisure.`,
    },
    {
      day: 2,
      title: `${p.name} Local Exploration`,
      content: `Full day sightseeing of major landmarks and cultural sites in ${p.name}.`,
    },
    {
      day: 3,
      title: 'Nature & Adventure',
      content: 'Explore the scenic beauty and hidden gems of the region with guided assistance.',
    },
    {
      day: 4,
      title: 'Cultural Immersion',
      content: 'Experience local traditions, cuisine, and artisanal crafts of the area.',
    },
    {
      day: 5,
      title: 'Shopping & Free Time',
      content: 'Day at leisure for personal exploration or souvenir shopping in local markets.',
    },
    {
      day: 6,
      title: 'Departure',
      content: `Check-out from hotel. Final transfer to the departure point with sweet memories of ${p.name}.`,
    },
  ]

  const quickFacts = p.quickFacts || [
    { label: 'Destination', value: p.location },
    { label: 'Duration', value: p.duration },
    { label: 'Tour Code', value: p.code || 'DHMY073' },
    { label: 'Tour Category', value: p.cat || 'International' },
    { label: 'Min Booking', value: p.minBooking || '2 Pax' },
    { label: 'Meal Plan', value: 'Breakfast Included' },
  ]

  const coreAttractions = (p.attractions && p.attractions.length > 0)
    ? p.attractions
    : (p.name.toLowerCase().includes('shanghai')
        ? ['The Bund & Huangpu River', 'Nanjing Road Pedestrian Street', 'Yu Garden & Old Town', 'West Lake (Hangzhou)', 'Lingyin Temple & Longjing Tea', 'Lujiazui Financial District & Skyline']
        : [
            'Historical Landmarks',
            'Scenic Viewpoints',
            'Local Markets & Food',
            'Cultural Heritage Sites',
            'City Center Exploration',
            'Guided Iconic Attractions',
          ])

  const availableGroups = [
    { date: 'June 04', status: 'Sold out', year: '2026' },
    { date: 'June 11', status: 'Sold out', year: '2026' },
    { date: 'July 09', status: 'Available', year: '2026' },
  ]

  if (loading) {
    return (
      <div
        className="package-details-loading"
        style={{
          minHeight: '60vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '16px',
          padding: '120px 20px',
        }}
      >
        <div
          style={{
            width: '44px',
            height: '44px',
            border: '4px solid #e2e8f0',
            borderTopColor: 'var(--brand)',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }}
        />
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        <p style={{ color: 'var(--stone)', fontSize: '15px', fontWeight: 600 }}>
          Loading package details...
        </p>
      </div>
    )
  }

  return (
    <div className="gt-details-page page-enter">
      {/* TOP HEADER */}
      <section
        className="top-header-bar"
        style={{
          position: 'relative',
          padding: '80px 0',
          overflow: 'hidden',
          background: '#0d1117',
          borderBottom: '1px solid rgba(2,2,191,0.1)',
        }}
      >
        {/* Background Image: Package banner */}
        {p.img && (
          <img
            src={p.img}
            alt={p.name}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center',
              zIndex: 0,
            }}
          />
        )}

        {/* Dark gradient overlay for text readability */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(180deg, rgba(13,17,23,0.65) 0%, rgba(13,17,23,0.88) 100%)',
            zIndex: 1,
          }}
        />

        <div className="container header-flex" style={{ position: 'relative', zIndex: 2 }}>
          <div
            className="duration-badge"
            style={{
              background: 'rgba(255,255,255,0.05)',
              backdropFilter: 'blur(10px)',
              borderColor: 'rgba(246,198,68,0.3)',
            }}
          >
            <span className="days" style={{ color: 'var(--secondary)' }}>
              {(p.duration || '0').split(' ')[0]}
            </span>
            <span className="label" style={{ color: 'rgba(255,255,255,0.6)' }}>
              Days
            </span>
            <div className="type" style={{ background: 'var(--secondary)', color: 'var(--ink)' }}>
              Group Tour
            </div>
          </div>

          <div className="title-section">
            <nav className="mini-crumbs" style={{ color: 'rgba(255,255,255,0.7)' }}>
              Home / {p.region || p.country || 'International'} / {p.name}
            </nav>
            <h1 className="main-title" style={{ color: 'var(--white)', fontSize: '32px' }}>
              {p.name}
            </h1>
            <div className="rating-row" style={{ color: 'var(--white)' }}>
              <Stars rating={p.rating || 4.8} />
              <span className="count" style={{ color: 'rgba(255,255,255,0.75)' }}>
                4.8 / 5.0 | <u>{p.reviews ? `${p.reviews} Reviews` : 'Verified Tour'}</u>
              </span>
            </div>
            <div className="itinerary-meta" style={{ color: 'var(--secondary)' }}>
              <Icon name="location" size={14} />
              <span>{p.location}</span>
            </div>
          </div>

          <div className="price-cta-box">
            <div className="price-info" style={{ color: 'var(--white)' }}>
              <span className="deal-label" style={{ color: '#ffc644', fontWeight: 'bold' }}>
                Super Deal Price
              </span>
              <span className="starts" style={{ opacity: 0.8 }}>
                starts from
              </span>
              <div className="amt-row" style={{ color: 'var(--white)' }}>
                <span className="curr">₹</span>
                <span className="val">{cleanPriceVal}</span>
              </div>
              <span className="note" style={{ opacity: 0.8 }}>
                per person on twin sharing
              </span>
            </div>
            <div className="cta-btns">
              <button
                className="book-top-btn"
                onClick={() => window.scrollTo({ top: 1500, behavior: 'smooth' })}
                style={{
                  boxShadow: '0 4px 15px rgba(255,182,0,0.3)',
                  background: '#ffc644',
                  color: '#0202bf',
                  fontWeight: '800',
                }}
              >
                Book Now
              </button>
              <button
                className="itinerary-top-btn"
                onClick={() => {
                  const el = document.querySelector('.itinerary-accordion-section')
                  if (el) el.scrollIntoView({ behavior: 'smooth' })
                }}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(5px)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  color: '#fff',
                  cursor: 'pointer',
                }}
              >
                View Your Itinerary
              </button>
            </div>
          </div>
        </div>
      </section>

      <div className="container">
        {/* HERO SECTION */}
        <section className="hero-media-section">
          <div className="main-gallery" style={{ position: 'relative' }}>
            <img
              src={allImages[currentImgIdx] || p.img}
              alt={p.name}
              style={{ width: '100%', height: '420px', objectFit: 'cover', borderRadius: '12px', display: 'block' }}
            />
            {allImages.length > 1 && (
              <>
                <button
                  className="prev-btn"
                  onClick={() => setCurrentImgIdx((prev) => (prev > 0 ? prev - 1 : allImages.length - 1))}
                  style={{ cursor: 'pointer' }}
                >
                  ‹
                </button>
                <button
                  className="next-btn"
                  onClick={() => setCurrentImgIdx((prev) => (prev < allImages.length - 1 ? prev + 1 : 0))}
                  style={{ cursor: 'pointer' }}
                >
                  ›
                </button>
              </>
            )}
            {allImages.length > 1 && (
              <div style={{ display: 'flex', gap: '8px', marginTop: '12px', overflowX: 'auto', paddingBottom: '4px' }}>
                {allImages.map((imgUrl, idx) => (
                  <div
                    key={idx}
                    onClick={() => setCurrentImgIdx(idx)}
                    style={{
                      width: '74px',
                      height: '52px',
                      borderRadius: '6px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      border: currentImgIdx === idx ? '2px solid var(--brand)' : '2px solid #e2e8f0',
                      opacity: currentImgIdx === idx ? 1 : 0.65,
                      flexShrink: 0,
                      transition: 'all 0.2s',
                    }}
                  >
                    <img src={imgUrl} alt={`thumb-${idx}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                ))}
              </div>
            )}
          </div>

          <aside className="trust-sidebar">
            <div className="brand-trust">
              <h3>Malaysia's No.1 Travel Brand</h3>
              <div className="stats-row">
                <div className="stat">
                  <strong>300K+</strong>
                  <span>Happy guests</span>
                </div>
                <div className="stat">
                  <strong>41K+</strong>
                  <span>Successful tours</span>
                </div>
                <div className="stat">
                  <strong>15+</strong>
                  <span>Years in Field</span>
                </div>
              </div>
              <div className="trust-btns">
                <button
                  className="enquire-yellow"
                  onClick={() =>
                    sendToWhatsApp(
                      `Hello DIRA HOLIDAYS! I am interested in the tour package: ${p.name}.`
                    )
                  }
                >
                  Enquire Now
                </button>
                <button
                  className="call-black"
                  onClick={() => (window.location.href = 'tel:+917092210922')}
                >
                  Call Now
                </button>
              </div>
            </div>

            <div className="speciality-trip">
              <h4>Speciality of this Trip</h4>
              <div className="icons-grid">
                <div className="spec-icon">
                  <div className="circle">
                    <Icon name="plane" />
                  </div>
                  <span>Flight</span>
                </div>
                <div className="spec-icon">
                  <div className="circle">
                    <Icon name="globe" />
                  </div>
                  <span>Visa</span>
                </div>
                <div className="spec-icon">
                  <div className="circle">
                    <Icon name="hotel" />
                  </div>
                  <span>Hotel</span>
                </div>
                <div className="spec-icon">
                  <div className="circle">
                    <Icon name="utensils" />
                  </div>
                  <span>Meals</span>
                </div>
                <div className="spec-icon">
                  <div className="circle">
                    <Icon name="bus" />
                  </div>
                  <span>Transport</span>
                </div>
                <div className="spec-icon">
                  <div className="circle">
                    <Icon name="camera" />
                  </div>
                  <span>Sightseeing</span>
                </div>
              </div>
            </div>

            <div className="why-choose">
              <h4>Why Choose DIRA HOLIDAYS?</h4>
              <div className="reason">
                <Icon name="check" size={14} />
                <span>Super Friendly Tour Manager throughout the trip.</span>
              </div>
              <div className="reason">
                <Icon name="check" size={14} />
                <span>Mouth-Watering Indian Food included in the trip.</span>
              </div>
              <div className="reason">
                <Icon name="check" size={14} />
                <span>Amazing Attractions and Sightseeing in the Trip.</span>
              </div>
            </div>
          </aside>
        </section>

        {/* QUICK FACTS & ATTRACTIONS */}
        <section className="facts-attractions">
          <div className="facts-col">
            <h3>Quick Facts</h3>
            <div className="facts-list">
              {quickFacts.map((f, i) => (
                <div key={i} className="fact-item">
                  <div className="dot">●</div>
                  <strong>{f.label}:</strong> {f.value}
                </div>
              ))}
            </div>
          </div>
          <div className="attractions-col">
            <h3>Core Attractions</h3>
            <div className="attractions-grid">
              {coreAttractions.map((a, i) => (
                <div key={i} className="attr-item">
                  <div className="dot">●</div>
                  <span>{a}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* AVAILABLE GROUPS (Dates) */}
        <section className="available-groups">
          <h3>Available Groups</h3>
          <div className="dates-row">
            {availableGroups.map((g, i) => (
              <div
                key={i}
                className={`date-block ${g.status === 'Sold out' ? 'sold-out' : ''} ${selectedDate === g.date ? 'active' : ''}`}
                onClick={() => g.status !== 'Sold out' && setSelectedDate(g.date)}
              >
                <span className="day-name">Thu</span>
                <span className="date-val">{g.date}</span>
                <span className="year">{g.year}</span>
                <span className="status">{g.status}</span>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* BOOK YOUR TOUR FORMS (Full Width Background but Inner Container) */}
      <section className="booking-forms-section">
        <div className="container">
          <h3>Book Your Tour</h3>
          <div className="forms-grid">
            <div className="booking-card">
              <form className="inner-form" onSubmit={handleBookNowSubmit}>
                <div className="input-group">
                  <label>Departure *</label>
                  <select value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)}>
                    <option value="July 09">July 09, 2026</option>
                  </select>
                </div>
                <div className="input-group">
                  <label>Adult *</label>
                  <input
                    type="number"
                    value={adultsCount}
                    onChange={(e) => setAdultsCount(e.target.value)}
                    required
                    min={1}
                  />
                </div>
                <div className="input-group">
                  <label>Children</label>
                  <input
                    type="number"
                    value={childrenCount}
                    onChange={(e) => setChildrenCount(e.target.value)}
                    min={0}
                  />
                </div>
                <div className="input-group">
                  <label>Infant</label>
                  <input
                    type="number"
                    value={infantCount}
                    onChange={(e) => setInfantCount(e.target.value)}
                    min={0}
                  />
                </div>
                <button type="submit" className="book-now-main-btn">
                  BOOK NOW
                </button>
              </form>
            </div>

            <div className="enquire-card">
              <form className="inner-form" onSubmit={handleEnquireSubmit}>
                <div
                  className="input-row"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '10px',
                    marginBottom: '15px',
                  }}
                >
                  <input
                    type="text"
                    placeholder="Name *"
                    required
                    value={enquireName}
                    onChange={(e) => setEnquireName(e.target.value)}
                  />
                  <input
                    type="tel"
                    placeholder="Phone *"
                    required
                    value={enquirePhone}
                    onChange={(e) => setEnquirePhone(e.target.value)}
                  />
                </div>
                <input
                  type="email"
                  placeholder="Email *"
                  required
                  style={{ marginBottom: '15px' }}
                  value={enquireEmail}
                  onChange={(e) => setEnquireEmail(e.target.value)}
                />
                <input
                  type="text"
                  placeholder="Your Tour *"
                  value={p.name}
                  readOnly
                  style={{ marginBottom: '15px' }}
                />
                <button type="submit" className="enquire-now-main-btn">
                  ENQUIRE NOW
                </button>
              </form>
            </div>

            <div className="summary-info-card">
              <div className="info-item">
                <Icon name="location" size={16} />
                <div>
                  <span>Trip From</span>
                  <strong>Chennai</strong>
                </div>
              </div>
              <div className="info-item">
                <Icon name="location" size={16} />
                <div>
                  <span>Trip To</span>
                  <strong>{p.location || p.region || p.name}</strong>
                </div>
              </div>
              <div className="info-item">
                <Icon name="users" size={16} />
                <div>
                  <span>No. of people</span>
                  <strong>02 Adults</strong>
                </div>
              </div>
              <div className="info-item">
                <Icon name="clock" size={16} />
                <div>
                  <span>Duration</span>
                  <strong>{p.duration}</strong>
                </div>
              </div>
              <div className="info-item">
                <Icon name="hotel" size={16} />
                <div>
                  <span>Accommodation</span>
                  <strong>Premium Stay</strong>
                </div>
              </div>
              <div className="info-item">
                <Icon name="bus" size={16} />
                <div>
                  <span>Transportation</span>
                  <strong>A/C Vehicle</strong>
                </div>
              </div>
              <div className="itinerary-summary">
                <h4>Itinerary Summary</h4>
                {itinerary.slice(0, 6).map((item) => (
                  <div key={item.day || item._id} className="summary-day">
                    <div className="dot">📍</div>
                    <span>
                      Day {String(item.day).padStart(2, '0')}: {item.title}
                    </span>
                  </div>
                ))}
                {itinerary.length > 6 && (
                  <u
                    className="view-more"
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      const el = document.querySelector('.itinerary-accordion-section')
                      if (el) el.scrollIntoView({ behavior: 'smooth' })
                    }}
                  >
                    View All {itinerary.length} Days
                  </u>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="container">
        {/* PACKAGE COST TABLE */}
        <section className="cost-table-section">
          <h4>Package Cost</h4>
          <table className="cost-table">
            <thead>
              <tr>
                <th>Adult</th>
                <th>Child With Bed</th>
                <th>Child Without Bed</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>₹ {priceNumber.toLocaleString('en-IN')}</td>
                <td>₹ {Math.max(10000, priceNumber - 5000).toLocaleString('en-IN')}</td>
                <td>₹ {Math.max(8000, priceNumber - 8000).toLocaleString('en-IN')}</td>
              </tr>
            </tbody>
          </table>
        </section>

        {/* PRICE DETAILS & EMI */}
        <section className="price-details-emi">
          <div className="price-details-box">
            <div className="box-header">Price Details</div>
            <div className="price-amt">₹ {priceNumber.toLocaleString('en-IN')}</div>
            <div className="sub-note">+5% GST & 5% TCS</div>
            <div className="reserve">Reserve your seat @</div>
            <div className="reserve-amt">₹ 5,000 / Person</div>
          </div>
          <div className="easy-emi-box">
            <div className="box-header">Easy EMI</div>
            <div className="starts">Starts from</div>
            <div className="price-amt">
              ₹ {Math.round(priceNumber / 12).toLocaleString('en-IN')} /mo
            </div>
            <div className="no-cost">No Cost EMI also available.</div>
            <button
              className="book-now-yellow"
              onClick={() =>
                sendToWhatsApp(
                  `Hello DIRA HOLIDAYS! I am interested in booking the tour "${p.name}" with easy payment options.`
                )
              }
              style={{
                width: '100%',
                background: 'var(--secondary)',
                border: 'none',
                padding: '15px',
                fontWeight: '800',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              BOOK NOW
            </button>
          </div>
        </section>

        {/* ITINERARY ACCORDION */}
        <section className="itinerary-accordion-section">
          <h3>Itinerary (Day Wise)</h3>
          <div className="itinerary-accordion-list">
            {itinerary.map((item) => (
              <div
                key={item.day || item._id}
                className={`itinerary-item ${openDay === item.day ? 'active' : ''}`}
              >
                <button
                  className="accordion-header"
                  onClick={() => setOpenDay(openDay === item.day ? null : item.day)}
                >
                  <div className="header-left">
                    <div className="pin">📍</div>
                    <span>
                      Day {String(item.day).padStart(2, '0')}: {item.title}
                    </span>
                  </div>
                  <span className="plus-icon">{openDay === item.day ? '-' : '+'}</span>
                </button>
                {openDay === item.day && (
                  <div className="accordion-content">
                    {item.points && item.points.length > 0 ? (
                      <ul
                        style={{
                          paddingLeft: '20px',
                          margin: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px',
                        }}
                      >
                        {item.points.map((pt, i) => (
                          <li key={i}>{pt}</li>
                        ))}
                      </ul>
                    ) : (
                      <p>{item.content}</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* TOUR DETAILS TABS */}
        <section className="tour-details-tabs">
          <h3>Tour Details</h3>
          <div className="tabs-header">
            <button
              className={tourDetailTab === 'departure' ? 'active' : ''}
              onClick={() => setTourDetailTab('departure')}
            >
              Departure & Arrival
            </button>
            <button
              className={tourDetailTab === 'hotel' ? 'active' : ''}
              onClick={() => setTourDetailTab('hotel')}
            >
              Accommodation Details
            </button>
            <button
              className={tourDetailTab === 'reporting' ? 'active' : ''}
              onClick={() => setTourDetailTab('reporting')}
            >
              Reporting & Dropping
            </button>
          </div>
          <div className="tab-panel">
            {tourDetailTab === 'departure' && (
              <div
                style={{
                  padding: '20px',
                  background: 'var(--surface)',
                  borderRadius: '8px',
                  marginTop: '20px',
                  fontSize: '14px',
                }}
              >
                <p>
                  <strong>Flights & Route:</strong> Scheduled flights and high-speed rail connections arranged for {p.name}.
                </p>
                <p style={{ marginTop: '10px' }}>
                  <strong>Ground Transportation:</strong> Private air-conditioned transfers throughout the entire tour with dedicated driver/coordinator.
                </p>
                <p style={{ marginTop: '10px', fontSize: '12px', color: 'var(--muted)' }}>
                  * Final flight timings and transport vouchers will be shared 48 hours before departure.
                </p>
              </div>
            )}
            {tourDetailTab === 'hotel' && (
              <div
                style={{
                  padding: '20px',
                  background: 'var(--surface)',
                  borderRadius: '8px',
                  marginTop: '20px',
                  fontSize: '14px',
                }}
              >
                <p>
                  <strong>Hotel Standard:</strong> Carefully selected 4-Star / Premium accommodations offering supreme comfort, cleanliness, and central connectivity.
                </p>
                <p style={{ marginTop: '10px' }}>
                  <strong>Room Type:</strong> Deluxe Room (Twin / Double Sharing) with private bathroom, air conditioning, and daily breakfast buffet.
                </p>
                <p style={{ marginTop: '10px', fontSize: '12px', color: 'var(--muted)' }}>
                  * Confirmed hotel name and contact details are provided upon trip voucher issuance.
                </p>
              </div>
            )}
            {tourDetailTab === 'reporting' && (
              <div
                style={{
                  padding: '20px',
                  background: 'var(--surface)',
                  borderRadius: '8px',
                  marginTop: '20px',
                  fontSize: '14px',
                }}
              >
                <p>
                  <strong>Reporting Point:</strong> Meet & Greet by our local tour coordinator upon arrival at destination airport.
                </p>
                <p style={{ marginTop: '10px' }}>
                  <strong>Dropping Point:</strong> Drop-off at the departure airport on Day {itinerary.length} in time for your return flight.
                </p>
                <p style={{ marginTop: '10px', fontSize: '12px', color: 'var(--muted)' }}>
                  * 24/7 emergency customer care and local concierge assistance available during your travels.
                </p>
              </div>
            )}
          </div>
        </section>

        {/* TOUR INFORMATION (Inclusions/Exclusions) */}
        <section className="tour-info-tabs" style={{ padding: '40px 0' }}>
          <h3>Tour Information</h3>
          <div
            className="info-tabs"
            style={{
              display: 'flex',
              gap: '20px',
              borderBottom: '1px solid var(--border)',
              marginBottom: '20px',
            }}
          >
            {['inclusions', 'exclusions', 'need to know', 'advance preparation'].map((t) => (
              <button
                key={t}
                className={activeTab === t ? 'active' : ''}
                onClick={() => setActiveTab(t)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '10px 0',
                  fontWeight: '700',
                  borderBottom:
                    activeTab === t ? '3px solid var(--secondary)' : '3px solid transparent',
                  cursor: 'pointer',
                }}
              >
                {t.toUpperCase()}
              </button>
            ))}
          </div>
          <div className="info-panel">
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {activeTab === 'inclusions' &&
                (p.inclusions ? (
                  p.inclusions.map((inc, i) => (
                    <li
                      key={i}
                      style={{
                        display: 'flex',
                        gap: '10px',
                        marginBottom: '12px',
                        fontSize: '14px',
                      }}
                    >
                      <Icon name="check" size={16} color="var(--brand-deep)" /> {inc}
                    </li>
                  ))
                ) : (
                  <>
                    <li
                      style={{
                        display: 'flex',
                        gap: '10px',
                        marginBottom: '12px',
                        fontSize: '14px',
                      }}
                    >
                      <Icon name="check" size={16} color="var(--brand-deep)" /> Round-trip flight
                      tickets for {p.name}.
                    </li>
                    <li
                      style={{
                        display: 'flex',
                        gap: '10px',
                        marginBottom: '12px',
                        fontSize: '14px',
                      }}
                    >
                      <Icon name="check" size={16} color="var(--brand-deep)" /> {p.duration}{' '}
                      accommodation with breakfast.
                    </li>
                    <li
                      style={{
                        display: 'flex',
                        gap: '10px',
                        marginBottom: '12px',
                        fontSize: '14px',
                      }}
                    >
                      <Icon name="check" size={16} color="var(--brand-deep)" /> Private AC Vehicle
                      for all transfers in {p.name}.
                    </li>
                    <li
                      style={{
                        display: 'flex',
                        gap: '10px',
                        marginBottom: '12px',
                        fontSize: '14px',
                      }}
                    >
                      <Icon name="check" size={16} color="var(--brand-deep)" /> Professional tour
                      guide and manager support.
                    </li>
                    <li
                      style={{
                        display: 'flex',
                        gap: '10px',
                        marginBottom: '12px',
                        fontSize: '14px',
                      }}
                    >
                      <Icon name="check" size={16} color="var(--brand-deep)" /> Entry tickets to all
                      major attractions mentioned in itinerary.
                    </li>
                    <li
                      style={{
                        display: 'flex',
                        gap: '10px',
                        marginBottom: '12px',
                        fontSize: '14px',
                      }}
                    >
                      <Icon name="check" size={16} color="var(--brand-deep)" /> 24/7 dedicated
                      travel assistance.
                    </li>
                  </>
                ))}

              {activeTab === 'exclusions' &&
                (p.exclusions ? (
                  p.exclusions.map((exc, i) => (
                    <li
                      key={i}
                      style={{
                        display: 'flex',
                        gap: '10px',
                        marginBottom: '12px',
                        fontSize: '14px',
                        opacity: 0.6,
                      }}
                    >
                      <Icon name="check" size={16} color="var(--muted)" /> {exc}
                    </li>
                  ))
                ) : (
                  <>
                    <li
                      style={{
                        display: 'flex',
                        gap: '10px',
                        marginBottom: '12px',
                        fontSize: '14px',
                        opacity: 0.6,
                      }}
                    >
                      <Icon name="check" size={16} color="var(--muted)" /> Airfare / Train tickets
                      (unless specified).
                    </li>
                    <li
                      style={{
                        display: 'flex',
                        gap: '10px',
                        marginBottom: '12px',
                        fontSize: '14px',
                        opacity: 0.6,
                      }}
                    >
                      <Icon name="check" size={16} color="var(--muted)" /> Personal expenses, tips,
                      and shopping.
                    </li>
                    <li
                      style={{
                        display: 'flex',
                        gap: '10px',
                        marginBottom: '12px',
                        fontSize: '14px',
                        opacity: 0.6,
                      }}
                    >
                      <Icon name="check" size={16} color="var(--muted)" /> Any activities not
                      mentioned in the itinerary.
                    </li>
                  </>
                ))}

              {activeTab === 'need to know' &&
                (p.policies ? (
                  p.policies.map((pol, i) => (
                    <li
                      key={i}
                      style={{
                        display: 'flex',
                        gap: '10px',
                        marginBottom: '12px',
                        fontSize: '14px',
                      }}
                    >
                      <Icon name="check" size={16} color="var(--brand-deep)" /> {pol}
                    </li>
                  ))
                ) : (
                  <>
                    <li
                      style={{
                        display: 'flex',
                        gap: '10px',
                        marginBottom: '12px',
                        fontSize: '14px',
                      }}
                    >
                      <Icon name="check" size={16} color="var(--brand-deep)" /> Valid ID proof is
                      mandatory for all guests.
                    </li>
                    <li
                      style={{
                        display: 'flex',
                        gap: '10px',
                        marginBottom: '12px',
                        fontSize: '14px',
                      }}
                    >
                      <Icon name="check" size={16} color="var(--brand-deep)" /> Hotel check-in is
                      usually 2:00 PM and check-out is 11:00 AM.
                    </li>
                  </>
                ))}

              {activeTab === 'advance preparation' && (
                <>
                  <li
                    style={{ display: 'flex', gap: '10px', marginBottom: '12px', fontSize: '14px' }}
                  >
                    <Icon name="check" size={16} color="var(--brand-deep)" /> Carry comfortable
                    walking shoes and light clothing.
                  </li>
                  <li
                    style={{ display: 'flex', gap: '10px', marginBottom: '12px', fontSize: '14px' }}
                  >
                    <Icon name="check" size={16} color="var(--brand-deep)" /> Check weather
                    conditions before packing.
                  </li>
                </>
              )}
            </ul>
          </div>
        </section>
      </div>

      {/* FOOTER ENQUIRE SECTION */}
      <section className="footer-enquire">
        <div className="container enquire-flex">
          <div className="contact-info">
            <h2>Stay Connected</h2>
            <div className="contact-row">
              <div className="icon-wrap">
                <Icon name="phone" />
              </div>
              <a href="tel:+917092210922" style={{ color: 'inherit', textDecoration: 'none' }} className="text">+91 70922 10922</a>
            </div>
            <div className="contact-row">
              <div className="icon-wrap">
                <Icon name="envelope" />
              </div>
              <a href="mailto:info@diraholidays.com" style={{ color: 'inherit', textDecoration: 'none' }} className="text">info@diraholidays.com</a>
            </div>
          </div>
          <div className="enquire-form-box">
            <h3>Book Your Dream Vacay Today!</h3>
            <form className="vacay-form" onSubmit={handleFooterEnquireSubmit}>
              <input
                type="text"
                placeholder="Name"
                required
                value={footerName}
                onChange={(e) => setFooterName(e.target.value)}
              />
              <input
                type="text"
                placeholder="City of Residence"
                required
                value={footerCity}
                onChange={(e) => setFooterCity(e.target.value)}
              />
              <input
                type="email"
                placeholder="Email"
                required
                value={footerEmail}
                onChange={(e) => setFooterEmail(e.target.value)}
              />
              <div className="phone-row">
                <select>
                  <option>🇮🇳 +91</option>
                </select>
                <input
                  type="tel"
                  placeholder="Phone Number"
                  required
                  value={footerPhone}
                  onChange={(e) => setFooterPhone(e.target.value)}
                />
              </div>
              <input
                type="text"
                placeholder="Travel Destination"
                value={footerDestination}
                onChange={(e) => setFooterDestination(e.target.value)}
              />
              <input
                type="date"
                placeholder="Date of Travel"
                required
                value={footerDate}
                onChange={(e) => setFooterDate(e.target.value)}
              />
              <input
                type="number"
                placeholder="No. of People"
                required
                value={footerPeopleCount}
                onChange={(e) => setFooterPeopleCount(e.target.value)}
              />
              <select
                required
                value={footerVacationType}
                onChange={(e) => setFooterVacationType(e.target.value)}
              >
                <option value="">Vacation Type *</option>
                <option>Family</option>
                <option>Honeymoon</option>
                <option>Group</option>
              </select>
              <div
                className="captcha-row-simple"
                style={{ marginBottom: '15px', fontSize: '14px', fontWeight: '700' }}
              >
                Captcha: 8 + 7 ={' '}
                <input
                  type="text"
                  required
                  value={footerCaptcha}
                  onChange={(e) => setFooterCaptcha(e.target.value)}
                  style={{ width: '60px', padding: '5px', marginLeft: '10px' }}
                />
              </div>
              <button type="submit" className="vacay-submit">
                SUBMIT
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  )
}
